import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Clock, 
  CalendarBlank, 
  ArrowLeft,
  SpinnerGap,
  CaretLeft,
  CaretRight,
  Export,
  FileText,
  FileCsv,
  TrendUp,
  Coffee,
  MapPin,
  PencilSimple,
  CheckCircle,
  X,
  Briefcase,
  Warning,
  ListChecks
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { timeTrackingAPI, applicationsAPI, jobsAPI } from '../api/api';
import { BreadcrumbsInline } from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Timesheets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [timesheet, setTimesheet] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: '', clock_out: '', notes: '' });
  const [exportLoading, setExportLoading] = useState(false);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  // Calculate week dates
  useEffect(() => {
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() - (7 * weekOffset));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    setDateRange({
      start: weekStart.toISOString().split('T')[0],
      end: weekEnd.toISOString().split('T')[0]
    });
  }, [weekOffset]);

  // Check user type and fetch jobs
  useEffect(() => {
    const type = localStorage.getItem('userType');
    setUserType(type);
    
    const fetchJobs = async () => {
      try {
        if (type === 'worker') {
          const response = await applicationsAPI.getMyApplications();
          const apps = response.data.applications || [];
          const accepted = apps.filter(app => app.status === 'accepted');
          
          const jobPromises = accepted.map(async (app) => {
            try {
              const jobRes = await jobsAPI.getById(app.job_id);
              return { id: app.job_id, title: jobRes.data.job?.title || 'Unknown Job' };
            } catch {
              return null;
            }
          });
          
          const jobsList = (await Promise.all(jobPromises)).filter(Boolean);
          setJobs(jobsList);
          if (jobsList.length > 0) {
            setSelectedJobId(jobsList[0].id);
          }
        } else {
          const response = await jobsAPI.getMyJobs({});
          const jobsList = (response.data.jobs || []).map(j => ({ id: j._id, title: j.title }));
          setJobs(jobsList);
          if (jobsList.length > 0) {
            setSelectedJobId(jobsList[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };
    
    fetchJobs();
  }, []);

  // Fetch timesheet
  const fetchTimesheet = useCallback(async () => {
    if (!selectedJobId || !dateRange.start || !dateRange.end) return;
    
    setLoading(true);
    try {
      const response = await timeTrackingAPI.getTimesheet(
        selectedJobId,
        dateRange.start,
        dateRange.end
      );
      
      if (response.data.success) {
        setTimesheet(response.data.timesheet);
      }
    } catch (error) {
      console.error('Error fetching timesheet:', error);
      setTimesheet(null);
    } finally {
      setLoading(false);
    }
  }, [selectedJobId, dateRange]);

  useEffect(() => {
    if (selectedJobId && dateRange.start) {
      fetchTimesheet();
    }
  }, [fetchTimesheet, selectedJobId, dateRange]);

  // Navigate weeks
  const navigateWeek = (direction) => {
    setWeekOffset(prev => prev + direction);
  };

  // Format date display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '0h';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  // Auto-fill missing days from completed daily checklists
  const handleAutoFill = async () => {
    if (!selectedJobId || !dateRange.start || !dateRange.end) return;
    setAutoFillLoading(true);
    try {
      const res = await timeTrackingAPI.generateFromChecklists({
        job_id: selectedJobId,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const { created, skipped_existing } = res.data;
      if (created > 0) {
        toast({
          title: `${created} time entr${created === 1 ? 'y' : 'ies'} generated`,
          description: 'Drafted from completed checklist tasks — review and edit as needed.',
        });
        fetchTimesheet();
      } else {
        toast({
          title: 'Nothing to fill',
          description: skipped_existing > 0
            ? 'All checklist days in this week already have time entries.'
            : 'No completed checklist tasks found for this job this week.',
        });
      }
    } catch (error) {
      toast({
        title: 'Auto-fill failed',
        description: error.response?.data?.detail || 'Could not generate entries from checklists',
        variant: 'destructive',
      });
    } finally {
      setAutoFillLoading(false);
    }
  };

  // Export timesheet
  const handleExport = async (format) => {
    if (!selectedJobId || !dateRange.start || !dateRange.end) return;
    
    setExportLoading(true);
    try {
      const response = await timeTrackingAPI.exportTimesheet(
        selectedJobId,
        dateRange.start,
        dateRange.end,
        null,
        format
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet_${dateRange.start}_${dateRange.end}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Export Complete', description: `Timesheet downloaded as ${format.toUpperCase()}` });
    } catch (error) {
      toast({ title: 'Export Failed', description: 'Could not export timesheet', variant: 'destructive' });
    } finally {
      setExportLoading(false);
    }
  };

  // Edit entry
  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      clock_in: entry.clock_in ? new Date(entry.clock_in).toISOString().slice(0, 16) : '',
      clock_out: entry.clock_out ? new Date(entry.clock_out).toISOString().slice(0, 16) : '',
      notes: entry.notes || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    try {
      const updateData = {};
      if (editForm.clock_in) updateData.clock_in = editForm.clock_in;
      if (editForm.clock_out) updateData.clock_out = editForm.clock_out;
      updateData.notes = editForm.notes;
      
      await timeTrackingAPI.editEntry(editingEntry._id, updateData);
      toast({ title: 'Entry Updated' });
      setShowEditModal(false);
      fetchTimesheet();
    } catch (error) {
      toast({ title: 'Update Failed', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2B4C6F] to-blue-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: userType === 'worker' ? '/worker-dashboard' : '/contractor-dashboard' },
              { label: 'Timesheets' }
            ]}
            className="mb-3"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <CalendarBlank size={32} weight="bold" />
              </div>
              <div>
                <h1 className="text-3xl font-black">Timesheets</h1>
                <p className="text-white/80">View and export your time records</p>
              </div>
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleAutoFill}
                disabled={autoFillLoading || !selectedJobId}
                variant="outline"
                className="bg-[#D95800] border-black text-white hover:bg-[#B84A00] font-bold"
                data-testid="autofill-from-checklists-btn"
                title="Generate missing time entries from completed daily checklists"
              >
                {autoFillLoading ? <SpinnerGap size={18} className="animate-spin" /> : <ListChecks size={18} weight="bold" />}
                <span className="ml-2 hidden sm:inline">Auto-fill from Checklists</span>
              </Button>
              <Button
                onClick={() => handleExport('pdf')}
                disabled={exportLoading || !timesheet}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                {exportLoading ? <SpinnerGap size={18} className="animate-spin" /> : <FileText size={18} weight="bold" />}
                <span className="ml-2 hidden sm:inline">PDF</span>
              </Button>
              <Button
                onClick={() => handleExport('csv')}
                disabled={exportLoading || !timesheet}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                {exportLoading ? <SpinnerGap size={18} className="animate-spin" /> : <FileCsv size={18} weight="bold" />}
                <span className="ml-2 hidden sm:inline">CSV</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] p-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            {/* Job Selector */}
            <div className="flex-1">
              <Label className="font-bold text-neutral-900 dark:text-white">Select Job</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger className="mt-1 h-12 border-2">
                  <SelectValue placeholder="Choose a job..." />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map(job => (
                    <SelectItem key={job.id} value={job.id}>
                      <div className="flex items-center gap-2">
                        <Briefcase size={16} weight="bold" />
                        {job.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Week Navigator */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigateWeek(1)}
                variant="outline"
                className="h-12 w-12 border-2 p-0"
              >
                <CaretLeft size={24} weight="bold" />
              </Button>
              <div className="text-center min-w-[200px] px-4 py-2 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Week of</p>
                <p className="font-bold text-neutral-900 dark:text-white">{formatDate(dateRange.start)}</p>
              </div>
              <Button
                onClick={() => navigateWeek(-1)}
                disabled={weekOffset === 0}
                variant="outline"
                className="h-12 w-12 border-2 p-0"
              >
                <CaretRight size={24} weight="bold" />
              </Button>
            </div>
          </div>
        </div>

        {/* Timesheet Content */}
        {loading ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-12 text-center">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F] mx-auto" />
          </div>
        ) : !timesheet || timesheet.entry_count === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-12 text-center">
            <Clock size={64} weight="light" className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">No Time Entries</h3>
            <p className="text-neutral-500">No time entries recorded for this period</p>
            {userType === 'worker' && (
              <Link to="/time-clock">
                <Button className="mt-4 bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000]">
                  Go to Time Clock
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[3px_3px_0px_#000]">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Hours</p>
                <p className="text-3xl font-black text-neutral-900 dark:text-white">
                  {timesheet.totals.total_hours}h
                </p>
              </div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[3px_3px_0px_#000]">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Regular Hours</p>
                <p className="text-3xl font-black text-emerald-500">
                  {timesheet.totals.regular_hours}h
                </p>
              </div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[3px_3px_0px_#000]">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Overtime</p>
                <p className="text-3xl font-black text-amber-500">
                  {timesheet.totals.overtime_hours}h
                </p>
                <p className="text-xs text-neutral-500">@ {timesheet.totals.overtime_multiplier}x</p>
              </div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 shadow-[3px_3px_0px_#000]">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Equivalent Regular</p>
                <p className="text-3xl font-black text-[#2B4C6F]">
                  {timesheet.totals.equivalent_regular_hours}h
                </p>
              </div>
            </div>

            {/* Daily Breakdown */}
            <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden">
              <div className="p-4 bg-neutral-50 dark:bg-neutral-700 border-b-2 border-black dark:border-neutral-600">
                <h3 className="font-black text-lg text-neutral-900 dark:text-white">Daily Breakdown</h3>
              </div>
              
              <div className="divide-y-2 divide-neutral-100 dark:divide-neutral-700">
                {timesheet.daily_summary.map(day => (
                  <div key={day.date} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-neutral-900 dark:text-white">{formatDate(day.date)}</h4>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">
                          <Clock size={14} weight="bold" className="inline mr-1" />
                          {formatDuration(day.total_work_minutes)}
                        </span>
                        {day.overtime_hours > 0 && (
                          <span className="text-amber-500 font-bold">
                            +{day.overtime_hours.toFixed(1)}h OT
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {day.entries.map(entry => (
                        <div 
                          key={entry._id}
                          className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#2B4C6F] rounded-lg flex items-center justify-center">
                              <Clock size={16} weight="bold" className="text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                                {formatTime(entry.clock_in)} → {formatTime(entry.clock_out)}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-neutral-500">
                                {entry.break_minutes > 0 && (
                                  <span><Coffee size={12} className="inline mr-1" />{formatDuration(entry.break_minutes)} break</span>
                                )}
                                {entry.gps_clock_in && (
                                  <span><MapPin size={12} className="inline mr-1" />GPS verified</span>
                                )}
                                {entry.source === 'checklist_auto' && (
                                  <span className="inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-bold px-1.5 py-0.5 rounded border border-orange-300 dark:border-orange-700" data-testid={`auto-entry-badge-${entry._id}`}>
                                    <ListChecks size={11} weight="bold" /> AUTO
                                  </span>
                                )}
                                {entry.notes && (
                                  <span className="truncate max-w-[150px]">Note: {entry.notes}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-neutral-900 dark:text-white">
                              {formatDuration(entry.work_minutes)}
                            </span>
                            <button
                              onClick={() => openEditModal(entry)}
                              className="opacity-0 group-hover:opacity-100 p-2 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg transition-opacity"
                              title="Edit Entry"
                            >
                              <PencilSimple size={16} weight="bold" className="text-neutral-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Entry Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-neutral-800 border-2 border-black shadow-[4px_4px_0px_#000]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <PencilSimple size={24} weight="bold" className="text-[#2B4C6F]" />
              Edit Time Entry
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
              <Warning size={18} weight="bold" className="text-amber-500 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                All edits are logged in an audit trail visible to both worker and contractor.
              </p>
            </div>
            
            <div>
              <Label className="font-bold">Clock In</Label>
              <Input
                type="datetime-local"
                value={editForm.clock_in}
                onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })}
                className="mt-1 h-12 border-2"
              />
            </div>
            
            <div>
              <Label className="font-bold">Clock Out</Label>
              <Input
                type="datetime-local"
                value={editForm.clock_out}
                onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })}
                className="mt-1 h-12 border-2"
              />
            </div>
            
            <div>
              <Label className="font-bold">Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Add notes..."
                className="mt-1 h-12 border-2"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000]"
            >
              <CheckCircle size={20} weight="bold" className="mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Timesheets;
